// src/pages/EmployerExpense.jsx

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  collection,
  onSnapshot,
  updateDoc,
  doc,
  query,
  getDoc,
  where,
  setDoc,
  serverTimestamp,
  Timestamp,
  arrayUnion,
  deleteField,
} from "firebase/firestore";
import { DateTime } from "luxon";
import { auth, db } from "../firebase/config";
import "../styles/EmployerExpense.css";
import {
  getExpenseConfigured,
  setExpenseConfigured,
} from "../utils/firstRunFlags";
import { FiSettings } from "react-icons/fi";
import { SlidersHorizontal } from "lucide-react";
import NoDataIllustration from "../tools/assets/undraw_credit-card_t6qm.svg";

/** Utility: format as money */
const money = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });

export default function EmployerExpense() {
  const [expenses, setExpenses] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");
  const [employeesMap, setEmployeesMap] = useState({}); // id -> employee doc data

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [authChecking, setAuthChecking] = useState(true);
  const [hasAnyExpenses, setHasAnyExpenses] = useState(false);
  const [expenseConfiguredFlag, setExpenseConfiguredFlag] = useState(false);

  // receipt viewer
  const [viewDocUrl, setViewDocUrl] = useState("");
  const [showModal, setShowModal] = useState(false);

  // table state
  const [selectedTab, setSelectedTab] = useState("Pending");
  const [employeeFilter, setEmployeeFilter] = useState("All");
  const [expenseTypeFilter, setExpenseTypeFilter] = useState("All");
  const [sortOrder, setSortOrder] = useState("asc");

  // filter drawer
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Reject / NeedsChanges modal
  const [decisionModal, setDecisionModal] = useState({
    open: false,
    mode: "Reject", // 'Reject' | 'NeedsChanges'
    expense: null,
    note: "",
  });

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // seed demo data
  const [isSeedingExpenses, setIsSeedingExpenses] = useState(false);

  // ── Budget UI / settings ─────────────────────────────────────────────
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [savingBudget, setSavingBudget] = useState(false);
  const [budget, setBudget] = useState({
    monthlyBudget: 0,
    yearlyBudget: 0,
    remainingMonthly: 0,
    remainingYearly: 0,
    monthKey: "", // "YYYY-MM"
    yearKey: "", // "YYYY"
  });
  // ── Settings dropdown + Expense config modal ─────────────────────────
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const settingsWrapRef = useRef(null);

  const [showExpenseConfigModal, setShowExpenseConfigModal] = useState(false);
  // ── Expense Config (Types + Payment Methods) ────────────────────────
  const [expenseConfigSaving, setExpenseConfigSaving] = useState(false);

  const [expenseConfig, setExpenseConfig] = useState({
    expenseTypes: [],
    paymentMethods: [],
  });

  const [newExpenseType, setNewExpenseType] = useState("");
  const [newPaymentMethod, setNewPaymentMethod] = useState("");

  // Soft first-run banner (dismissible)
  const [expenseBannerOpen, setExpenseBannerOpen] = useState(false);

  const openBudgetSettings = useCallback(() => {
    setShowBudgetModal(true);
  }, []);

  // Header helpers
  const nowTZ = useCallback(() => DateTime.now().setZone(companyTimeZone), [
    companyTimeZone,
  ]);
  const currentMonthKey = useCallback(() => nowTZ().toFormat("yyyy-LL"), [
    nowTZ,
  ]);
  const currentYearKey = useCallback(() => nowTZ().toFormat("yyyy"), [nowTZ]);

  // Format expense date as MM/DD/YYYY in company TZ
  const fmtDate = useCallback(
    (exp) => {
      const zone = companyTimeZone || "UTC";
      let dt = null;

      if (exp?.expenseDateTs?.toDate) {
        dt = DateTime.fromJSDate(exp.expenseDateTs.toDate()).setZone(zone);
      } else if (exp?.expenseDate) {
        dt = DateTime.fromISO(String(exp.expenseDate), { zone }).setZone(zone);
        if (!dt.isValid) {
          dt = DateTime.fromJSDate(new Date(exp.expenseDate)).setZone(zone);
        }
      }

      return dt && dt.isValid ? dt.toFormat("MM/dd/yyyy") : "—";
    },
    [companyTimeZone],
  );

  // Notify the employee about status change
  // ✅ EMPLOYEE BELL NOTIFICATION (writes to users/{employeeId}/notifications)
  const notifyEmployeeOnStatus = useCallback(
    async (exp, status, note = "") => {
      try {
        if (!exp?.employeeId || !companyId) return;

        // ✅ deterministic id (no duplicates if action runs twice)
        const notifId = `expenseStatus_${exp.id}_${status}`;

        const expType = exp.expenseType || "expense";
        const amt = money(exp.amount || 0);

        let title = "Expense updated";
        let type = "expenseStatus";
        let message = `Your ${expType} expense (${amt}) is now ${status}.`;

        if (status === "Approved") {
          title = "Expense approved";
          type = "expenseApproved";
          message = `Your ${expType} expense for ${amt} was approved.`;
        } else if (status === "Rejected") {
          title = "Expense rejected";
          type = "expenseRejected";
          message = ` Your ${expType} expense for ${amt} was rejected.`;
        } else if (status === "NeedsChanges") {
          title = "Changes requested";
          type = "expenseNeedsChanges";
          message = `📝 Changes were requested for your ${expType} expense for ${amt}.`;
        }

        await setDoc(
          doc(db, `users/${exp.employeeId}/notifications`, notifId),
          {
            archived: false,
            read: false,

            companyId,
            employeeId: exp.employeeId,

            type, // "expenseApproved" | "expenseRejected" | "expenseNeedsChanges"
            title,
            message,

            expenseId: exp.id,
            status, // Approved | Rejected | NeedsChanges
            note: note || "",

            createdAt: serverTimestamp(), // ✅ THIS is what your bell orders by
          },
          { merge: true },
        );
      } catch (e) {
        console.error("Failed to notify employee:", e);
      }
    },
    [companyId],
  );

  // ── Auth → get company + timezone ────────────────────────────────────
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setError("Employer not authenticated.");
        setAuthChecking(false);
        return;
      }

      try {
        const employerRef = doc(db, "users", user.uid);
        const employerDoc = await getDoc(employerRef);
        if (!employerDoc.exists()) throw new Error("Company not found.");

        const employerData = employerDoc.data();
        const cid = employerData.companyId;
        setCompanyId(cid);

        // Load saved filters (per user)
        const savedFilters = employerData.expenseFilters;
        if (savedFilters && savedFilters.companyId === cid) {
          if (savedFilters.selectedTab)
            setSelectedTab(savedFilters.selectedTab);
          if (savedFilters.employeeFilter)
            setEmployeeFilter(savedFilters.employeeFilter);
          if (savedFilters.expenseTypeFilter)
            setExpenseTypeFilter(savedFilters.expenseTypeFilter);
          if (savedFilters.sortOrder) setSortOrder(savedFilters.sortOrder);
          if (savedFilters.pageSize) setPageSize(savedFilters.pageSize);
        }

        // company timezone (used for month/year rollover)
        const companyRef = doc(db, "companies", cid);
        const companySnap = await getDoc(companyRef);
        setCompanyTimeZone(
          companySnap.exists() ? companySnap.data()?.timeZone || "UTC" : "UTC",
        );
      } catch (err) {
        setError(err.message || "Failed to resolve company.");
      } finally {
        setAuthChecking(false);
      }
    });

    return () => unsubscribe();
  }, []);
  // Close settings dropdown on outside click / ESC
  useEffect(() => {
    const onDown = (e) => {
      if (!showSettingsMenu) return;

      const wrap = settingsWrapRef.current;
      if (wrap && !wrap.contains(e.target)) {
        setShowSettingsMenu(false);
      }
    };

    const onKey = (e) => {
      if (!showSettingsMenu) return;
      if (e.key === "Escape") setShowSettingsMenu(false);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showSettingsMenu]);

  useEffect(() => {
    if (!companyId) return;

    const ref = collection(db, `companies/${companyId}/expenses`);

    const unsub = onSnapshot(
      query(ref),
      (snap) => setHasAnyExpenses(!snap.empty),
      () => setHasAnyExpenses(false),
    );

    return () => unsub();
  }, [companyId]);

  // ── Load employees map for department lookups ───────────────────────
  useEffect(() => {
    if (!companyId) return;

    const employeesRef = collection(db, `companies/${companyId}/employees`);
    const unsub = onSnapshot(
      employeesRef,
      (snap) => {
        const map = {};
        snap.forEach((docSnap) => {
          map[docSnap.id] = { id: docSnap.id, ...docSnap.data() };
        });
        setEmployeesMap(map);
      },
      (err) => {
        console.error("Failed to load employees for Expense:", err);
      },
    );

    return () => unsub();
  }, [companyId]);

  // ── Budget fetching + rollover ───────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;

    const loadBudget = async () => {
      try {
        const refDoc = doc(
          db,
          `companies/${companyId}/settings`,
          "expense_budget",
        );
        const snap = await getDoc(refDoc);

        const monthNow = currentMonthKey();
        const yearNow = currentYearKey();

        if (!snap.exists()) {
          const seed = {
            monthlyBudget: 0,
            yearlyBudget: 0,
            remainingMonthly: 0,
            remainingYearly: 0,
            monthKey: monthNow,
            yearKey: yearNow,
            updatedAt: serverTimestamp(),
          };
          await setDoc(refDoc, seed, { merge: true });
          setBudget(seed);
          return;
        }

        const data = snap.data() || {};
        let {
          monthlyBudget = 0,
          yearlyBudget = 0,
          remainingMonthly = 0,
          remainingYearly = 0,
          monthKey = "",
          yearKey = "",
        } = data;

        // rollover month
        if (monthKey !== monthNow) {
          remainingMonthly = monthlyBudget;
          monthKey = monthNow;
        }

        // rollover year
        if (yearKey !== yearNow) {
          remainingYearly = yearlyBudget;
          yearKey = yearNow;
        }

        // persist if rollover happened
        if (monthKey !== data.monthKey || yearKey !== data.yearKey) {
          await setDoc(
            refDoc,
            {
              monthlyBudget,
              yearlyBudget,
              remainingMonthly,
              remainingYearly,
              monthKey,
              yearKey,
              updatedAt: serverTimestamp(),
            },
            { merge: true },
          );
        }

        setBudget({
          monthlyBudget,
          yearlyBudget,
          remainingMonthly,
          remainingYearly,
          monthKey,
          yearKey,
        });
      } catch (e) {
        console.error("Failed to load budget:", e);
      }
    };

    loadBudget();
    const t = setInterval(loadBudget, 5 * 60 * 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, companyTimeZone]);
  // ── Load Expense Config (Types + Payment Methods) ───────────────────
  useEffect(() => {
    if (!companyId) return;

    const loadConfig = async () => {
      try {
        const refDoc = doc(
          db,
          `companies/${companyId}/settings`,
          "expense_config",
        );

        const snap = await getDoc(refDoc);

        if (!snap.exists()) {
          const seed = defaultExpenseConfig();
          await setDoc(
            refDoc,
            { ...seed, updatedAt: serverTimestamp() },
            { merge: true },
          );
          setExpenseConfig(seed);
          return;
        }

        const data = snap.data() || {};
        const types = Array.isArray(data.expenseTypes) ? data.expenseTypes : [];
        const methods = Array.isArray(data.paymentMethods)
          ? data.paymentMethods
          : [];

        // Safety: if doc exists but arrays missing/empty, seed defaults
        if (types.length === 0 && methods.length === 0) {
          const seed = defaultExpenseConfig();
          await setDoc(
            refDoc,
            { ...seed, updatedAt: serverTimestamp() },
            { merge: true },
          );
          setExpenseConfig(seed);
          return;
        }

        setExpenseConfig({
          expenseTypes: types,
          paymentMethods: methods,
        });
      } catch (e) {
        console.error("Failed to load expense config:", e);
      }
    };

    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;

    const ref = doc(db, "companies", companyId, "firstRun", "expense");

    const unsub = onSnapshot(
      ref,
      (snap) => {
        setExpenseConfiguredFlag(!!snap.data()?.configured);
      },
      () => setExpenseConfiguredFlag(false),
    );

    return () => unsub();
  }, [companyId]);

  const handleSaveExpenseConfig = async () => {
    if (!companyId) return;

    try {
      setExpenseConfigSaving(true);

      const refDoc = doc(
        db,
        `companies/${companyId}/settings`,
        "expense_config",
      );

      // Clean + normalize
      const cleanedTypes = (expenseConfig.expenseTypes || [])
        .map((t) => ({
          id: t.id || slugify(t.label),
          label: String(t.label || "").trim(),
          active: t.active !== false,
        }))
        .filter((t) => t.label);

      const cleanedMethods = (expenseConfig.paymentMethods || [])
        .map((m) => ({
          id: m.id || slugify(m.label),
          label: String(m.label || "").trim(),
          active: m.active !== false,
        }))
        .filter((m) => m.label);

      await setDoc(
        refDoc,
        {
          expenseTypes: cleanedTypes,
          paymentMethods: cleanedMethods,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      setExpenseConfig({
        expenseTypes: cleanedTypes,
        paymentMethods: cleanedMethods,
      });
      try {
        await setExpenseConfigured(db, companyId);
      } catch {}

      toast.success("Expense configuration saved.");
      setShowExpenseConfigModal(false);
    } catch (e) {
      console.error(e);
      toast.error("Failed to save expense configuration.");
    } finally {
      setExpenseConfigSaving(false);
    }
  };

  // ── Save budget: increase limit without clearing used amount ─────────
  const handleSaveBudget = async () => {
    if (!companyId) return;

    try {
      setSavingBudget(true);

      const refDoc = doc(
        db,
        `companies/${companyId}/settings`,
        "expense_budget",
      );

      const monthNow = currentMonthKey();
      const yearNow = currentYearKey();

      const snap = await getDoc(refDoc);
      const prev = snap.exists() ? snap.data() : {};

      const prevMonthKey = prev.monthKey || monthNow;
      const prevYearKey = prev.yearKey || yearNow;

      const prevMonthlyBudget = Number(prev.monthlyBudget || 0);
      const prevYearlyBudget = Number(prev.yearlyBudget || 0);

      const prevRemainingMonthly =
        prev.remainingMonthly != null
          ? Number(prev.remainingMonthly)
          : prevMonthlyBudget;
      const prevRemainingYearly =
        prev.remainingYearly != null
          ? Number(prev.remainingYearly)
          : prevYearlyBudget;

      const newMonthlyBudget = Number(budget.monthlyBudget || 0);
      const newYearlyBudget = Number(budget.yearlyBudget || 0);

      // same month => preserve used amount
      let newRemainingMonthly;
      if (prevMonthKey === monthNow) {
        const usedMonthly = Math.max(
          0,
          prevMonthlyBudget - prevRemainingMonthly,
        );
        newRemainingMonthly =
          newMonthlyBudget > 0
            ? Math.max(0, newMonthlyBudget - usedMonthly)
            : 0;
      } else {
        newRemainingMonthly = newMonthlyBudget;
      }

      // same year => preserve used amount
      let newRemainingYearly;
      if (prevYearKey === yearNow) {
        const usedYearly = Math.max(0, prevYearlyBudget - prevRemainingYearly);
        newRemainingYearly =
          newYearlyBudget > 0 ? Math.max(0, newYearlyBudget - usedYearly) : 0;
      } else {
        newRemainingYearly = newYearlyBudget;
      }

      await setDoc(
        refDoc,
        {
          monthlyBudget: newMonthlyBudget,
          yearlyBudget: newYearlyBudget,
          remainingMonthly: newRemainingMonthly,
          remainingYearly: newRemainingYearly,
          monthKey: monthNow,
          yearKey: yearNow,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      // Mark onboarding complete + hide banner
      try {
        await setExpenseConfigured(db, companyId);
      } catch {}
      setExpenseBannerOpen(false);

      setShowBudgetModal(false);
      toast.success("Expense budget saved.");

      setBudget((b) => ({
        ...b,
        monthlyBudget: newMonthlyBudget,
        yearlyBudget: newYearlyBudget,
        remainingMonthly: newRemainingMonthly,
        remainingYearly: newRemainingYearly,
        monthKey: monthNow,
        yearKey: yearNow,
      }));
    } catch (e) {
      console.error(e);
      toast.error("Failed to save budget.");
    } finally {
      setSavingBudget(false);
    }
  };

  const paymentMethodLabelFor = useCallback(
    (exp) => {
      // If you store label directly, use it
      if (exp?.paymentMethodLabel) return exp.paymentMethodLabel;

      // Otherwise resolve id -> label from current config
      const id = exp?.paymentMethod;
      if (!id) return "—";

      const found = (expenseConfig.paymentMethods || []).find(
        (m) => m.id === id,
      );
      return found?.label || id; // fallback to id if label missing
    },
    [expenseConfig.paymentMethods],
  );

  // ── Seed fake expenses for this company (dev / load testing) ─────────
  const handleSeedExpenses = async () => {
    if (!companyId) {
      toast.error("Company not resolved yet.");
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      toast.error("You must be logged in as an employer to seed data.");
      return;
    }

    try {
      setIsSeedingExpenses(true);
      const token = await user.getIdToken();

      const url =
        "https://us-central1-employee-b47f3.cloudfunctions.net/seedTestExpenses";

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          companyId,
          daysBack: 90,
          maxPerDay: 15,
        }),
      });

      let data = {};
      try {
        data = await res.json();
      } catch (_) {}

      if (!res.ok) {
        console.error("seedTestExpenses error:", data);
        toast.error(data.error || "Failed to seed expenses.");
        return;
      }

      toast.success(`Seeded ${data.created || 0} test expenses.`);
    } catch (err) {
      console.error("handleSeedExpenses error:", err);
      toast.error("Unexpected error seeding expenses.");
    } finally {
      setIsSeedingExpenses(false);
    }
  };

  // ── Live expenses by selected status ─────────────────────────────────
  useEffect(() => {
    if (authChecking || !companyId) return;

    setLoading(true);

    const expenseRef = collection(db, `companies/${companyId}/expenses`);
    const expenseQuery = query(expenseRef, where("status", "==", selectedTab));

    const unsub = onSnapshot(
      expenseQuery,
      (snapshot) => {
        const enriched = snapshot.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          const emp = employeesMap[data.employeeId] || {};
          const department =
            emp.group || emp.department || emp.departmentName || "—";
          return {
            id: docSnap.id,
            ...data,
            employeeName: data.employeeName || "Unknown",
            department,
          };
        });

        setExpenses(enriched);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setError("Failed to load expenses (live).");
        setLoading(false);
      },
    );

    return () => unsub();
  }, [authChecking, companyId, selectedTab, employeesMap]);

  // Soft first-run: banner + open settings once
  useEffect(() => {
    if (!companyId) return;

    (async () => {
      try {
        const configured = await getExpenseConfigured(db, companyId);
        if (!configured) {
          setExpenseBannerOpen(true);
          openBudgetSettings();
        }
      } catch (e) {
        console.warn("Expense first-run check failed:", e);
      }
    })();
  }, [companyId, openBudgetSettings]);

  // ── Approve / Reject (and budget deduction on approve) ──────────────
  const handleExpenseAction = async (expense, action) => {
    try {
      if (!companyId) return;

      const expenseRef = doc(db, `companies/${companyId}/expenses`, expense.id);

      // 1) Update expense
      await updateDoc(expenseRef, {
        status: action,
        updatedAt: serverTimestamp(),
        lastDecisionAt: serverTimestamp(),
        openChangeRequest: deleteField(),
        reviewerNote: deleteField(),
        approval: arrayUnion({
          approverId: auth.currentUser?.uid || "unknown",
          decision: action,
          note: "",
          at: Timestamp.now(),
        }),
      });

      // 2) History snapshot
      const historyRef = doc(
        collection(db, `companies/${companyId}/expense_history`),
        expense.id,
      );
      await setDoc(
        historyRef,
        {
          ...expense,
          status: action,
          decisionDate: serverTimestamp(),
        },
        { merge: true },
      );

      // 3) If approved: deduct budget
      if (action === "Approved") {
        try {
          const bRef = doc(
            db,
            `companies/${companyId}/settings`,
            "expense_budget",
          );
          const snap = await getDoc(bRef);

          const monthNow = currentMonthKey();
          const yearNow = currentYearKey();

          let {
            monthlyBudget = 0,
            yearlyBudget = 0,
            remainingMonthly = 0,
            remainingYearly = 0,
            monthKey = monthNow,
            yearKey = yearNow,
          } = snap.exists() ? snap.data() : {};

          // rollover safety
          if (monthKey !== monthNow) {
            remainingMonthly = monthlyBudget;
            monthKey = monthNow;
          }
          if (yearKey !== yearNow) {
            remainingYearly = yearlyBudget;
            yearKey = yearNow;
          }

          const amt = Number(expense.amount || 0);

          const willExceedMonthly =
            monthlyBudget > 0 && amt > Number(remainingMonthly || 0);
          const willExceedYearly =
            yearlyBudget > 0 && amt > Number(remainingYearly || 0);

          if (willExceedMonthly || willExceedYearly) {
            const scopes = [];
            if (willExceedMonthly) scopes.push("monthly");
            if (willExceedYearly) scopes.push("yearly");
            toast.warning(
              `Approving this will exceed your ${scopes.join(
                " & ",
              )} budget. You can increase the limit in Expense Budget Settings.`,
            );
          }

          const newRemainingMonthly = Number(remainingMonthly || 0) - amt;
          const newRemainingYearly = Number(remainingYearly || 0) - amt;

          await setDoc(
            bRef,
            {
              monthlyBudget: Number(monthlyBudget),
              yearlyBudget: Number(yearlyBudget),
              remainingMonthly: newRemainingMonthly,
              remainingYearly: newRemainingYearly,
              monthKey,
              yearKey,
              updatedAt: serverTimestamp(),
            },
            { merge: true },
          );

          setBudget((b) => ({
            ...b,
            monthlyBudget: Number(monthlyBudget),
            yearlyBudget: Number(yearlyBudget),
            remainingMonthly: newRemainingMonthly,
            remainingYearly: newRemainingYearly,
            monthKey,
            yearKey,
          }));
        } catch (e) {
          console.warn("Budget deduction failed:", e);
        }
      }

      await notifyEmployeeOnStatus(expense, action);
      toast.success(`Expense ${action.toLowerCase()} successfully.`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update expense status.");
    }
  };

  // Reject with reason OR Request Changes
  const handleExpenseDecision = async (expense, mode, note) => {
    const status = mode === "Reject" ? "Rejected" : "NeedsChanges";

    try {
      const expenseRef = doc(db, `companies/${companyId}/expenses`, expense.id);

      await updateDoc(expenseRef, {
        status,
        reviewerNote: note || "",
        openChangeRequest:
          status === "NeedsChanges"
            ? {
                by: auth.currentUser?.uid || "unknown",
                note: note || "",
                at: Timestamp.now(),
              }
            : deleteField(),
        updatedAt: serverTimestamp(),
        lastDecisionAt: serverTimestamp(),
        approval: arrayUnion({
          approverId: auth.currentUser?.uid || "unknown",
          decision: status,
          note: note || "",
          at: Timestamp.now(),
        }),
      });

      await notifyEmployeeOnStatus(expense, status, note || "");

      toast.success(
        status === "Rejected"
          ? "Rejected with reason sent."
          : "Change request sent.",
      );
    } catch (e) {
      console.error(e);
      toast.error("Failed to submit decision.");
    }
  };

  const handleApplyFilters = async () => {
    setShowFilterModal(false);

    const user = auth.currentUser;
    if (!user || !companyId) return;

    const payload = {
      companyId,
      selectedTab,
      employeeFilter,
      expenseTypeFilter,
      sortOrder,
      pageSize,
    };

    try {
      const userRef = doc(db, "users", user.uid);
      await setDoc(userRef, { expenseFilters: payload }, { merge: true });
    } catch (e) {
      console.error("Failed to save expense filters", e);
    }
  };

  const resetFilters = async () => {
    setEmployeeFilter("All");
    setExpenseTypeFilter("All");
    setSortOrder("asc");
    setSelectedTab("Pending");
    setPageSize(25);
    setPage(1);
    setShowFilterModal(false);

    const user = auth.currentUser;
    if (!user) return;

    try {
      const userRef = doc(db, "users", user.uid);
      await setDoc(userRef, { expenseFilters: deleteField() }, { merge: true });
    } catch (e) {
      console.error("Failed to clear expense filters", e);
    }
  };

  // ── Filters + sort ──────────────────────────────────────────────────
  const filteredSortedExpenses = useMemo(() => {
    return [...expenses]
      .filter((exp) =>
        expenseTypeFilter === "All"
          ? true
          : exp.expenseType === expenseTypeFilter,
      )
      .filter((exp) =>
        employeeFilter === "All" ? true : exp.employeeName === employeeFilter,
      )
      .sort((a, b) => {
        const aDate =
          a.expenseDateTs?.toDate?.() ?? new Date(a.expenseDate || 0);
        const bDate =
          b.expenseDateTs?.toDate?.() ?? new Date(b.expenseDate || 0);
        return sortOrder === "asc" ? aDate - bDate : bDate - aDate;
      });
  }, [expenses, expenseTypeFilter, employeeFilter, sortOrder]);

  // ── Pagination ──────────────────────────────────────────────────────
  const totalExpenses = filteredSortedExpenses.length;
  const totalPages = Math.max(1, Math.ceil(totalExpenses / pageSize));

  useEffect(() => {
    setPage(1);
  }, [expenseTypeFilter, employeeFilter, sortOrder, selectedTab, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const startIndex = (page - 1) * pageSize;
  const pagedExpenses = useMemo(
    () => filteredSortedExpenses.slice(startIndex, startIndex + pageSize),
    [filteredSortedExpenses, startIndex, pageSize],
  );

  const slugify = (s) =>
    String(s || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

  const defaultExpenseConfig = () => ({
    expenseTypes: [
      { id: "travel", label: "Travel", active: true },
      { id: "meals", label: "Meals", active: true },
      { id: "office_supplies", label: "Office Supplies", active: true },
      { id: "other", label: "Other", active: true },
    ],
    paymentMethods: [
      { id: "company_card", label: "Company Card", active: true },
      { id: "reimbursable", label: "Reimbursable", active: true },
      { id: "cash", label: "Cash", active: true },
      { id: "bank_transfer", label: "Bank Transfer", active: true },
    ],
  });

  const addExpenseType = () => {
    const label = String(newExpenseType || "").trim();
    if (!label) return;

    const id = slugify(label);
    setExpenseConfig((c) => {
      const exists = (c.expenseTypes || []).some((t) => t.id === id);
      if (exists) {
        toast.info("That expense type already exists.");
        return c;
      }
      return {
        ...c,
        expenseTypes: [...(c.expenseTypes || []), { id, label, active: true }],
      };
    });
    setNewExpenseType("");
  };

  const addPaymentMethod = () => {
    const label = String(newPaymentMethod || "").trim();
    if (!label) return;

    const id = slugify(label);
    setExpenseConfig((c) => {
      const exists = (c.paymentMethods || []).some((m) => m.id === id);
      if (exists) {
        toast.info("That payment method already exists.");
        return c;
      }
      return {
        ...c,
        paymentMethods: [
          ...(c.paymentMethods || []),
          { id, label, active: true },
        ],
      };
    });
    setNewPaymentMethod("");
  };

  const toggleExpenseType = (id) => {
    setExpenseConfig((c) => ({
      ...c,
      expenseTypes: (c.expenseTypes || []).map((t) =>
        t.id === id ? { ...t, active: !t.active } : t,
      ),
    }));
  };

  const togglePaymentMethod = (id) => {
    setExpenseConfig((c) => ({
      ...c,
      paymentMethods: (c.paymentMethods || []).map((m) =>
        m.id === id ? { ...m, active: !m.active } : m,
      ),
    }));
  };

  const removeExpenseType = (id) => {
    setExpenseConfig((c) => ({
      ...c,
      expenseTypes: (c.expenseTypes || []).filter((t) => t.id !== id),
    }));
  };

  const removePaymentMethod = (id) => {
    setExpenseConfig((c) => ({
      ...c,
      paymentMethods: (c.paymentMethods || []).filter((m) => m.id !== id),
    }));
  };

  // progress helpers
  const pct = (used, total) =>
    total > 0 ? Math.min(100, Math.max(0, (used / total) * 100)) : 0;

  const monthlyUsed =
    Number(budget.monthlyBudget || 0) - Number(budget.remainingMonthly || 0);
  const yearlyUsed =
    Number(budget.yearlyBudget || 0) - Number(budget.remainingYearly || 0);

  const uniqueEmployees = useMemo(
    () =>
      Array.from(new Set(expenses.map((e) => e.employeeName))).filter(Boolean),
    [expenses],
  );

  // ─────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="expense-title-container">
      <div className="expense-wrapper">
        <ToastContainer />

        {/* Receipt viewer modal */}
        {showModal && (
          <div className="expense-modal-overlay">
            <div className="expense-modal-content">
              <button
                className="expense-modal-close"
                onClick={() => setShowModal(false)}
              >
                ✖
              </button>
              <iframe
                src={viewDocUrl}
                width="100%"
                height="500px"
                title="Receipt Viewer"
              />
            </div>
          </div>
        )}

        {/* Decision modal (Reject / NeedsChanges) */}
        {decisionModal.open && (
          <div className="expense-modal-overlay">
            <div className="expense-modal-content">
              <button
                className="expense-modal-close"
                onClick={() =>
                  setDecisionModal({
                    open: false,
                    mode: "Reject",
                    expense: null,
                    note: "",
                  })
                }
              >
                ✖
              </button>

              <h3 style={{ marginTop: 0 }}>
                {decisionModal.mode === "Reject"
                  ? "Reject Expense"
                  : "Request Changes"}
              </h3>

              <p style={{ margin: "6px 0 10px" }}>
                Add a note for the employee:
              </p>

              <textarea
                value={decisionModal.note}
                onChange={(e) =>
                  setDecisionModal((p) => ({ ...p, note: e.target.value }))
                }
                rows={4}
                style={{ width: "100%" }}
                placeholder={
                  decisionModal.mode === "Reject"
                    ? "Reason for rejection…"
                    : "What should be changed or clarified?"
                }
              />

              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  gap: 8,
                  justifyContent: "flex-end",
                }}
              >
                <button
                  className="expense-btn-reject"
                  onClick={async () => {
                    const mode = decisionModal.mode;
                    const exp = decisionModal.expense;
                    await handleExpenseDecision(exp, mode, decisionModal.note);
                    setDecisionModal({
                      open: false,
                      mode: "Reject",
                      expense: null,
                      note: "",
                    });
                  }}
                >
                  {decisionModal.mode === "Reject" ? "Reject" : "Send Request"}
                </button>

                <button
                  className="expense-btn-approve"
                  onClick={() =>
                    setDecisionModal({
                      open: false,
                      mode: "Reject",
                      expense: null,
                      note: "",
                    })
                  }
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filter drawer */}
        {showFilterModal && (
          <div
            className="expense-filter-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowFilterModal(false);
            }}
          >
            <div className="expense-filter-modal">
              <div className="expense-filter-header">
                <h3>Filter &amp; Sort</h3>
                <button
                  className="expense-filter-close"
                  onClick={() => setShowFilterModal(false)}
                >
                  ✖
                </button>
              </div>

              <div className="expense-filter-grid">
                <div className="expense-filter-field">
                  <span>Employee</span>
                  <select
                    value={employeeFilter}
                    onChange={(e) => setEmployeeFilter(e.target.value)}
                  >
                    <option value="All">All</option>
                    {uniqueEmployees.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="expense-filter-field">
                  <span>Expense Type</span>
                  <select
                    value={expenseTypeFilter}
                    onChange={(e) => setExpenseTypeFilter(e.target.value)}
                  >
                    <option value="All">All</option>

                    {(expenseConfig.expenseTypes || [])
                      .filter((t) => t.active)
                      .map((t) => (
                        <option key={t.id} value={t.label}>
                          {t.label}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="expense-filter-field">
                  <span>Sort by Date</span>
                  <div className="expense-filter-sort-buttons">
                    <button
                      type="button"
                      onClick={() => setSortOrder("asc")}
                      className={
                        sortOrder === "asc" ? "active-sort" : undefined
                      }
                    >
                      Ascending
                    </button>
                    <button
                      type="button"
                      onClick={() => setSortOrder("desc")}
                      className={
                        sortOrder === "desc" ? "active-sort" : undefined
                      }
                    >
                      Descending
                    </button>
                  </div>
                </div>
              </div>

              <div className="expense-filter-actions">
                <button
                  type="button"
                  className="expense-filter-reset"
                  onClick={resetFilters}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="expense-filter-apply"
                  onClick={handleApplyFilters}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="expense-header">
          <div>
            <div className="expense-subtitle">
              Time zone: {companyTimeZone} • {nowTZ().toFormat("DDD t")}
            </div>
          </div>
        </div>

        {/* Soft first-run banner */}
        {expenseBannerOpen && (
          <div
            style={{
              marginTop: 12,
              padding: "10px 12px",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              background: "#f8fafc",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
            role="status"
            aria-live="polite"
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span aria-hidden>💳</span>
              <div>
                <strong>Complete Expense setup</strong>
                <div style={{ color: "#64748b", fontSize: 13 }}>
                  Set monthly &amp; yearly budgets. Saving updates your limits
                  and keeps what has already been used this period.
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="expense-settings-btn"
                onClick={openBudgetSettings}
                title="Expense Budget Settings"
              >
                Open Settings
              </button>
              <button
                type="button"
                className="timesheetbtn ghost"
                onClick={() => setExpenseBannerOpen(false)}
                title="Hide reminder for now"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Budget cards */}
        <div className="budget-grid">
          <div className="budget-card">
            <div className="budget-head">
              <div>Monthly Budget</div>
              <div className="budget-amount">
                {money(budget.remainingMonthly)} <span>remaining</span>
              </div>
              <div className="budget-sub">
                {money(monthlyUsed)} used of {money(budget.monthlyBudget)}
              </div>
            </div>

            <div className="budget-meter">
              <span
                style={{ width: `${pct(monthlyUsed, budget.monthlyBudget)}%` }}
              />
            </div>

            {budget.monthlyBudget > 0 && budget.remainingMonthly <= 0 && (
              <div className="budget-warning">
                Monthly budget reached.{" "}
                <button
                  type="button"
                  className="doc-link"
                  onClick={openBudgetSettings}
                >
                  Increase limit
                </button>
              </div>
            )}

            <div className="budget-foot">
              Period: {nowTZ().toFormat("LLLL yyyy")}
            </div>
          </div>

          <div className="budget-card">
            <div className="budget-head">
              <div>Yearly Budget</div>
              <div className="budget-amount">
                {money(budget.remainingYearly)} <span>remaining</span>
              </div>
              <div className="budget-sub">
                {money(yearlyUsed)} used of {money(budget.yearlyBudget)}
              </div>
            </div>

            <div className="budget-meter">
              <span
                style={{ width: `${pct(yearlyUsed, budget.yearlyBudget)}%` }}
              />
            </div>

            <div className="budget-foot">Year: {nowTZ().toFormat("yyyy")}</div>
          </div>
        </div>

        {/* Tabs + Settings + Filter */}
        <div className="expense-tabs">
          <div className="expense-tabs-left">
            {["Pending", "NeedsChanges", "Approved", "Rejected"].map((tab) => (
              <button
                key={tab}
                className={`expense-tab ${tab === selectedTab ? "active" : ""}`}
                onClick={() => setSelectedTab(tab)}
                type="button"
              >
                {tab}
              </button>
            ))}

            {false && (
              <button
                className="expense-seed-btn"
                onClick={handleSeedExpenses}
                disabled={!companyId || isSeedingExpenses}
                title="Seed test expenses for this company"
                type="button"
              >
                {isSeedingExpenses ? "Seeding…" : "Seed Test Expenses"}
              </button>
            )}
          </div>

          <div className="expense-tabs-right">
            <div className="expense-settings-wrap" ref={settingsWrapRef}>
              <button
                className="expense-settings-btn expense-settings-gear"
                onClick={() => setShowSettingsMenu((v) => !v)}
                title="Expense settings"
                aria-label="Expense settings"
                type="button"
              >
                <FiSettings size={18} />
              </button>

              {showSettingsMenu && (
                <div className="expense-settings-menu" role="menu">
                  <button
                    type="button"
                    className="expense-settings-item"
                    onClick={() => {
                      setShowSettingsMenu(false);
                      openBudgetSettings();
                    }}
                    role="menuitem"
                  >
                    Budget Settings
                  </button>

                  <button
                    type="button"
                    className="expense-settings-item"
                    onClick={() => {
                      setShowSettingsMenu(false);
                      setShowExpenseConfigModal(true);
                    }}
                    role="menuitem"
                  >
                    Expense Methods Settings
                  </button>
                </div>
              )}
            </div>

            <button
              className="expense-filter-btn"
              onClick={() => setShowFilterModal(true)}
              title="Filter & sort expenses"
              type="button"
            >
              <SlidersHorizontal size={16} />
              <span>Filter</span>
            </button>
          </div>
        </div>

        {/* Table */}
        {authChecking || loading ? (
          <p className="loading-text">Loading expenses...</p>
        ) : error ? (
          <p className="error-text">{error}</p>
        ) : filteredSortedExpenses.length === 0 ? (
          !hasAnyExpenses ? (
            // ✅ TRUE EMPTY
            <div className="ex-empty">
              <img
                src={NoDataIllustration}
                alt="No expenses yet"
                className="ex-empty__img"
              />

              <h3 className="ex-empty__title">No expenses yet</h3>

              <p className="ex-empty__text">
                Once employees submit expenses, you’ll review them here and keep
                spending aligned with your budget.
              </p>

              <div className="ex-empty__steps">
                <div
                  className={`ex-empty__step ${
                    expenseConfiguredFlag ? "done" : ""
                  }`}
                >
                  <span
                    className={`ex-empty__chip ${
                      expenseConfiguredFlag ? "done" : ""
                    }`}
                  >
                    {expenseConfiguredFlag ? "✓" : "1"}
                  </span>
                  {expenseConfiguredFlag
                    ? "Budgets and expense method configured"
                    : "Configure budgets and expense method"}
                </div>

                <div className="ex-empty__step">
                  <span className="ex-empty__chip">2</span>
                  Wait for employee to submit request
                </div>

                <div className="ex-empty__step">
                  <span className="ex-empty__chip">3</span>
                  Start reviewing & approving expenses
                </div>
              </div>

              <div className="ex-empty__hint">
                Tip: Configure your budget and expense method settings first so
                employees can submit expenses using the correct types and
                payment methods.
              </div>
            </div>
          ) : (
            // ✅ FILTERED EMPTY
            <div className="ex-noresults">
              <div className="ex-noresults__title">No matching expenses</div>
              <div className="ex-noresults__text">
                Try another tab or adjust your filters.
              </div>
            </div>
          )
        ) : (
          <div className="expense-card">
            <div className="expense-table-scroll">
              <table className="expense-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Department</th>
                    <th>Expense Type</th>
                    <th>Payment Method</th>

                    <th>Amount</th>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Receipt</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {pagedExpenses.map((exp) => (
                    <tr key={exp.id}>
                      <td>{exp.employeeName}</td>
                      <td>{exp.department || "—"}</td>
                      <td>{exp.expenseType}</td>
                      <td>{paymentMethodLabelFor(exp)}</td>

                      <td>{money(exp.amount)}</td>
                      <td>{fmtDate(exp)}</td>
                      <td>{exp.description}</td>

                      <td>
                        {exp.receiptUrl ? (
                          <button
                            className="doc-link"
                            type="button"
                            onClick={() => {
                              setViewDocUrl(exp.receiptUrl);
                              setShowModal(true);
                            }}
                          >
                            📎 View
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>

                      <td>
                        {exp.status === "Pending" ? (
                          <div
                            className="expense-action-buttons"
                            style={{ gap: 6 }}
                          >
                            <button
                              className="expense-btn-approve"
                              type="button"
                              onClick={() =>
                                handleExpenseAction(exp, "Approved")
                              }
                            >
                              Approve
                            </button>

                            <button
                              className="expense-btn-reject"
                              type="button"
                              onClick={() =>
                                setDecisionModal({
                                  open: true,
                                  mode: "Reject",
                                  expense: exp,
                                  note: "",
                                })
                              }
                            >
                              Reject
                            </button>

                            <button
                              className="doc-link"
                              type="button"
                              onClick={() =>
                                setDecisionModal({
                                  open: true,
                                  mode: "NeedsChanges",
                                  expense: exp,
                                  note: "",
                                })
                              }
                              title="Ask employee to edit and resubmit"
                            >
                              Request Changes
                            </button>
                          </div>
                        ) : (
                          <span
                            className={`expense-status-badge ${
                              exp.status === "Approved"
                                ? "approved"
                                : exp.status === "Rejected"
                                ? "rejected"
                                : ""
                            }`}
                          >
                            {exp.status}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalExpenses > pageSize && (
              <div className="expense-pagination-bar">
                <div className="expense-pagination-info">
                  Showing {startIndex + 1}–
                  {Math.min(startIndex + pageSize, totalExpenses)} of{" "}
                  {totalExpenses}
                </div>

                <div className="expense-pagination-controls">
                  <button
                    type="button"
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                  >
                    ⟪ First
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    ‹ Prev
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Next ›
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage(totalPages)}
                    disabled={page === totalPages}
                  >
                    Last ⟫
                  </button>

                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value) || 25);
                      setPage(1);
                    }}
                  >
                    <option value={25}>25 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Budget Settings Modal */}
        {showBudgetModal && (
          <div className="eb-backdrop">
            <div
              className="eb-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="eb-title"
            >
              <div className="eb-header">
                <div>
                  <div id="eb-title" className="eb-title">
                    Expense Budget Settings
                  </div>
                  <div className="eb-sub">
                    Remaining values reset automatically at the start of each
                    period • Time zone: <strong>{companyTimeZone}</strong>
                  </div>
                </div>

                <button
                  className="eb-close"
                  type="button"
                  aria-label="Close expense budget settings"
                  onClick={() => setShowBudgetModal(false)}
                >
                  ✕
                </button>
              </div>

              <div className="eb-banner">
                Set your monthly and yearly budgets. Saving updates your limits
                and keeps what has already been used this period.
              </div>

              <div className="eb-grid">
                <label className="eb-field">
                  <span className="eb-label">Monthly Budget</span>
                  <div className="eb-input">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={budget.monthlyBudget}
                      onChange={(e) =>
                        setBudget((b) => ({
                          ...b,
                          monthlyBudget: e.target.value,
                        }))
                      }
                      inputMode="decimal"
                    />
                    <span className="eb-suffix">USD</span>
                  </div>
                  <span className="eb-help">Set 0 for no monthly cap</span>
                </label>

                <label className="eb-field">
                  <span className="eb-label">Yearly Budget</span>
                  <div className="eb-input">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={budget.yearlyBudget}
                      onChange={(e) =>
                        setBudget((b) => ({
                          ...b,
                          yearlyBudget: e.target.value,
                        }))
                      }
                      inputMode="decimal"
                    />
                    <span className="eb-suffix">USD</span>
                  </div>
                  <span className="eb-help">Set 0 for no yearly cap</span>
                </label>
              </div>

              <div className="eb-remaining">
                <div>
                  Current remaining (month):{" "}
                  <strong>{money(budget.remainingMonthly)}</strong>
                </div>
                <div>
                  Current remaining (year):{" "}
                  <strong>{money(budget.remainingYearly)}</strong>
                </div>
              </div>

              <div className="eb-footer">
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => setShowBudgetModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn primary"
                  onClick={handleSaveBudget}
                  disabled={savingBudget}
                >
                  {savingBudget ? "Saving…" : "Save Settings"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showExpenseConfigModal && (
          <div className="eb-backdrop">
            <div
              className="eb-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ec-title"
            >
              {/* Header (fixed) */}
              <div className="eb-header">
                <div className="eb-head-left">
                  <div id="ec-title" className="eb-title">
                    Expense Types &amp; Payment Methods
                  </div>
                  <div className="eb-sub">
                    Configure what employees can submit and how expenses are
                    paid • Time zone: <strong>{companyTimeZone}</strong>
                  </div>
                </div>

                <button
                  className="eb-close"
                  type="button"
                  aria-label="Close expense configuration"
                  onClick={() => setShowExpenseConfigModal(false)}
                >
                  ✕
                </button>
              </div>

              {/* Body (scrollable) */}
              <div className="eb-body">
                <div className="eb-banner">
                  Choose which expense types employees can use, and which
                  payment methods are available.
                </div>

                <div className="ec-content">
                  <div className="ec-grid">
                    {/* Expense Types */}
                    <section className="ec-panel">
                      <div className="ec-panel-head">
                        <strong className="ec-panel-title">
                          Expense Types
                        </strong>
                        <div className="ec-panel-hint">
                          Toggle to enable/disable
                        </div>
                      </div>

                      <div className="ec-add-row">
                        <input
                          className="ec-input"
                          value={newExpenseType}
                          onChange={(e) => setNewExpenseType(e.target.value)}
                          placeholder="Add a new expense type…"
                        />
                        <button
                          type="button"
                          className="ec-btn ec-btn-primary"
                          onClick={addExpenseType}
                        >
                          Add
                        </button>
                      </div>

                      <div className="ec-list">
                        {(expenseConfig.expenseTypes || []).map((t) => (
                          <div
                            key={t.id}
                            className={`ec-item ${
                              t.active ? "" : "is-disabled"
                            }`}
                          >
                            <div className="ec-item-left">
                              <button
                                type="button"
                                className="ec-toggle"
                                onClick={() => toggleExpenseType(t.id)}
                                title="Enable/Disable"
                                aria-label={`Toggle expense type: ${t.label}`}
                              >
                                {t.active ? "✅" : "🚫"}
                              </button>

                              <div className="ec-item-label">{t.label}</div>

                              {!t.active && (
                                <span className="ec-item-badge">Disabled</span>
                              )}
                            </div>

                            <button
                              type="button"
                              className="ec-remove"
                              onClick={() => removeExpenseType(t.id)}
                              title="Remove type"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </section>

                    {/* Payment Methods */}
                    <section className="ec-panel">
                      <div className="ec-panel-head">
                        <strong className="ec-panel-title">
                          Payment Methods
                        </strong>
                        <div className="ec-panel-hint">
                          Toggle to enable/disable
                        </div>
                      </div>

                      <div className="ec-add-row">
                        <input
                          className="ec-input"
                          value={newPaymentMethod}
                          onChange={(e) => setNewPaymentMethod(e.target.value)}
                          placeholder="Add a new payment method…"
                        />
                        <button
                          type="button"
                          className="ec-btn ec-btn-primary"
                          onClick={addPaymentMethod}
                        >
                          Add
                        </button>
                      </div>

                      <div className="ec-list">
                        {(expenseConfig.paymentMethods || []).map((m) => (
                          <div
                            key={m.id}
                            className={`ec-item ${
                              m.active ? "" : "is-disabled"
                            }`}
                          >
                            <div className="ec-item-left">
                              <button
                                type="button"
                                className="ec-toggle"
                                onClick={() => togglePaymentMethod(m.id)}
                                title="Enable/Disable"
                                aria-label={`Toggle payment method: ${m.label}`}
                              >
                                {m.active ? "✅" : "🚫"}
                              </button>

                              <div className="ec-item-label">{m.label}</div>

                              {!m.active && (
                                <span className="ec-item-badge">Disabled</span>
                              )}
                            </div>

                            <button
                              type="button"
                              className="ec-remove"
                              onClick={() => removePaymentMethod(m.id)}
                              title="Remove method"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </section>
                  </div>
                </div>
              </div>

              {/* Footer (fixed) */}
              <div className="eb-footer">
                <button
                  type="button"
                  className="ec-btn ec-btn-ghost"
                  onClick={() => setShowExpenseConfigModal(false)}
                >
                  Close
                </button>

                <button
                  type="button"
                  className="ec-btn ec-btn-primary"
                  onClick={handleSaveExpenseConfig}
                  disabled={expenseConfigSaving}
                >
                  {expenseConfigSaving ? "Saving…" : "Save Settings"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
